import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UserContext } from 'context';

import i18n from 'i18n-js';
import { useQuery } from 'hooks';
import { buildUrl } from 'utils';
import Title from 'components/Title';
import { Table } from 'components/UI';
import Layout from 'components/Layout';
import { withRouter } from 'react-router-dom';
import OrgAbility from 'components/OrgAbility';
import Tooltip from '@material-ui/core/Tooltip';
import RepeatIcon from '@material-ui/icons/Repeat';
import SearchIcon from '@material-ui/icons/Search';
import useStyles from './VehiclesGroupsList.styled';
import { SearchField } from 'components/FormFields';
import IconButton from '@material-ui/core/IconButton';
import { vehicleActions } from 'state/modules/vehicles';
import { toQueryString, truncateKeywordSpaces } from 'utils';
import { ChangeVehicleStatusModal } from 'components/Modals';
import InputAdornment from '@material-ui/core/InputAdornment';
import { VehicleGroupsLabel } from 'components/VehiclesGroups';
import { Grid, Paper, Button, Container } from '@material-ui/core';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';

const VehiclesGroupsList = ({ history }) => {
  const classes = useStyles();
  const urlParams = useQuery();
  const { orgType } = useContext(UserContext);
  const { core } = useSelector((state) => state.config?.settings || {});
  const hasPricingRulesSetEnabled = core?.pricing?.pricing_rules_set || false;

  const dispatch = useDispatch();

  const { list, pagination, loadingList } = useSelector((state) => state.vehiclesGroups);
  const [openChangeVehiclesStatus, setOpenChangeVehiclesStatus] = useState(false);
  const [vehicleGroupId, setVehicleGroupId] = useState(null);
  const [vehicleGroupTZ, setVehicleGroupTZ] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
  });

  const ActionsMenu = ({ row }) => {
    const handleClick = () => handleModalOpen(row);

    return (
      <Tooltip title={i18n.t('common.change_status')}>
        <IconButton
          variant="contained"
          aria-haspopup="true"
          onClick={handleClick}
          aria-controls="card-menu"
          className={classes.cardHeaderActions}
          data-cy={`vg-change-status-${row?.name?.replace(' ', '-')}`}
        >
          <RepeatIcon />
        </IconButton>
      </Tooltip>
    );
  };

  /**
   * Table columns
   * Includes fleet and service groups
   * Show or hide columns with omit key
   */
  const columns = [
    {
      name: i18n.t('common.name'),
      selector: 'name',
      sortable: true,
      grow: 2,
    },
    {
      name: i18n.t('common.fleet_owner'),
      selector: 'parent.name',
      format: (row) =>
        orgType === 'msServiceOrg' && row?.parent?.organization_name
          ? row?.parent?.organization_name
          : '--',
      sortable: false,
      omit: orgType !== 'msServiceOrg',
      grow: 1,
    },
    {
      name: i18n.t('common.fleet'),
      selector: 'parent.name',
      format: (row) => (orgType === 'msServiceOrg' && row?.parent?.name ? row?.parent?.name : '--'),
      sortable: false,
      omit: orgType !== 'msServiceOrg',
      grow: 1,
    },
    {
      name: i18n.t('vehicle_groups.pricing_rule_set'),
      selector: 'current_pricing_rule_set.name',
      format: (row) =>
        orgType !== 'msOwnerOrg' && row?.current_pricing_rule_set?.name
          ? row?.current_pricing_rule_set?.name
          : '--',
      sortable: false,
      omit: !hasPricingRulesSetEnabled,
      grow: 1,
    },
    {
      name: i18n.t('vehicle_groups.location'),
      selector: 'primary_location.name',
      format: (row) =>
        orgType !== 'msOwnerOrg' && row?.primary_location?.name
          ? row?.primary_location?.name
          : '--',
      sortable: false,
      omit: orgType === 'msOwnerOrg',
      grow: 2,
    },
    {
      name: i18n.t('common.time_zone'),
      selector: 'time_zone',
      sortable: true,
      grow: 0,
    },
    {
      name: i18n.t('vehicle_groups.customer_access'),
      selector: 'driver_access',
      format: (row) => (row.driver_access ? i18n.t('common.yes') : i18n.t('common.no')),
      sortable: true,
      omit: orgType === 'msOwnerOrg',
    },
    {
      name: i18n.t('common.actions'),
      cell: (row) => <ActionsMenu row={row} />,
      ignoreRowClick: true,
      allowOverflow: true,
      sortable: false,
      grow: 0,
      center: true,
    },
  ];

  /**
   * Get vehicles grpups list
   * @param {object} params Request params
   */
  const fetchVehiclesGroups = useCallback(
    (params = {}) => {
      const _params = {
        ...params,
        group_type: 'CUSTOMER',
      };
      if (_params?.keyword) {
        _params.keyword = truncateKeywordSpaces(_params.keyword);
      }
      dispatch(VehiclesGroupsActions.fetchVehicleGroups(_params));
    },
    [dispatch, orgType]
  );

  // Fetch on load/ params change
  useEffect(() => {
    if (orgType) {
      fetchVehiclesGroups(params);
    }
  }, [fetchVehiclesGroups, params, orgType]);

  const handleParams = (params) => {
    const data = { ...params };
    setParams({ ...params, ...data });
    const _params = toQueryString(data);
    history.replace(buildUrl(`/service-groups${_params}`));
  };

  /**
   * Handle page change on table
   * @param {number} page Number of the page
   */
  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle sort change on table
   * @param {string} column Column of the table
   * @param {string} sortDirection Sort direction
   */
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  /**
   * Handle per rows (limit) change on table
   * @param {number} limit New limit value
   */
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle search filter
   * @param {string} name Name of the field to search
   * @param {string} value Value of the input
   */
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach((key) => !filters[key] && delete filters[key]);
    handleParams({ ...filters });
  };

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(buildUrl(`/service-groups/${row.id}`));
  };

  const handleModalOpen = (row) => {
    dispatch(vehicleActions.resetChangeStatus());
    setVehicleGroupId(row?.id);
    setVehicleGroupTZ(row?.time_zone);
    setOpenChangeVehiclesStatus((prevState) => !prevState);
  };

  return (
    <Layout title={VehicleGroupsLabel({ plural: true })}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
              <Grid item sm={8}>
                <Title title={i18n.t('vehicle_groups.manage_service_groups')} />
              </Grid>
              <OrgAbility subject={['msServiceOrg', 'plainOrg']}>
                <Grid item>
                  <Button
                    data-cy="create-service-group"
                    onClick={() => {
                      dispatch(VehiclesGroupsActions.resetOnboarding());
                      history.push(buildUrl('/service-groups/create'));
                    }}
                    variant="contained"
                    color="primary"
                    style={{ textTransform: 'inherit' }}
                  >
                    {i18n.t('vehicle_groups.create_vehicle_groups')}
                  </Button>
                </Grid>
              </OrgAbility>
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ paddingBottom: '25px' }}>
            <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end" spacing={2}>
              <Grid item xs={12} sm={4} md={3}>
                <SearchField
                  label={i18n.t('vehicle_groups.search', {
                    type: VehicleGroupsLabel({ plural: true })?.toLowerCase(),
                  })}
                  field={{
                    name: 'keyword',
                    onChange: search,
                    value: params.keyword,
                  }}
                  inputProps={{
                    startAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.searchIcon,
                        }}
                        position="start"
                      >
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                data={list}
                columns={columns}
                page={params.page}
                onSort={handleSort}
                limit={params.limit}
                loading={loadingList}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                defaultSortAsc={!params?.sort_desc}
                defaultSortField={params?.sort_field}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
              />
            </Paper>
          </Grid>
        </Grid>
        {openChangeVehiclesStatus && (
          <ChangeVehicleStatusModal
            timezone={vehicleGroupTZ}
            handleClick={handleModalOpen}
            vehicleGroupId={vehicleGroupId}
            title={i18n.t('vehicle.change_vehicles_status')}
            open={vehicleGroupId !== null && openChangeVehiclesStatus}
          />
        )}
      </Container>
    </Layout>
  );
};

export default withRouter(VehiclesGroupsList);
